import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer } from "lucide-react";

const QR_URL = "https://centrodiparodontologia.com/test-gengive?utm_source=volantino&utm_medium=qr";
const VARIANTS = {
  studio: {
    label: "Sala d'attesa / farmacie",
    eyebrow: "Test gratuito · 1 minuto",
    title: ["Le tue gengive", "sono a rischio?"],
    signs: ["Sanguinano quando lavi i denti?", "Alito cattivo che non passa?", "Un dente che si muove?"],
    text: "Inquadra il codice con la fotocamera del telefono: 8 domande semplici, risultato immediato e i consigli del Dott. Nicola Mauro.",
  },
  medici: {
    label: "Studi medici (diabete, gravidanza)",
    eyebrow: "Un consiglio del tuo medico",
    title: ["Diabete, gravidanza,", "gengive che sanguinano?"],
    signs: ["Il diabete aumenta il rischio di parodontite", "In gravidanza le gengive si infiammano più facilmente", "Gengive sane aiutano anche la salute generale"],
    text: "Scopri in 1 minuto se le tue gengive hanno bisogno di attenzione: inquadra il codice con la fotocamera del telefono.",
  },
};

const PERKS = [
  ["Laser, senza bisturi", "Cura della parodontite con laser e microscopio, senza punti."],
  ["Se hai paura, va bene", "Prima si parla, poi si decide. Puoi fermarti quando vuoi."],
  ["Igiene GBT", "Pulizia professionale delicata con protocollo certificato."],
];

const Flyer = ({ v }) => (
  <article className="flyer mx-auto bg-[#FCFBF7] text-[#1B3B28] flex flex-col" data-testid="flyer-sheet">
    <div className="flex-1 p-[14mm] flex flex-col">
      <p className="text-[11pt] tracking-[0.25em] uppercase text-[#3E6B48]">{v.eyebrow}</p>
      <h1 className="f-serif text-[40pt] leading-[0.98] mt-[6mm]">{v.title[0]}<br /><em className="text-[#3E6B48]">{v.title[1]}</em></h1>
      <ul className="mt-[9mm] space-y-[3mm]">
        {v.signs.map((s) => <li key={s} className="flex items-center gap-[3mm] text-[14pt]"><span className="w-[3mm] h-[3mm] rounded-full bg-[#86C89B] shrink-0" />{s}</li>)}
      </ul>
      <div className="mt-[12mm] grid grid-cols-3 gap-[4mm]">
        {PERKS.map(([t, d]) => (
          <div key={t} className="rounded-[5mm] bg-[#E6ECE4] p-[5mm]">
            <p className="f-serif text-[15pt] leading-tight">{t}</p>
            <p className="text-[9.5pt] text-[#526356] mt-[2mm] leading-snug">{d}</p>
          </div>
        ))}
      </div>
      <div className="mt-auto flex items-end gap-[10mm] pt-[10mm]">
        <div className="bg-white p-[4mm] rounded-[4mm] border border-[#1B3B28]/15 shrink-0" data-testid="flyer-qr">
          <QRCodeSVG value={QR_URL} size={220} level="M" fgColor="#0F2317" />
        </div>
        <div>
          <p className="f-serif text-[22pt] leading-tight">Fai il test<br />delle gengive</p>
          <p className="text-[11pt] text-[#526356] mt-[3mm] leading-snug">{v.text}</p>
          <p className="text-[10pt] text-[#3E6B48] mt-[3mm]">centrodiparodontologia.com/test-gengive</p>
        </div>
      </div>
    </div>
    <footer className="bg-[#1B3B28] text-[#FCFBF7] px-[14mm] py-[7mm] flex justify-between items-end gap-[6mm]">
      <div>
        <p className="f-serif text-[16pt] leading-tight">Dott. Nicola Mauro</p>
        <p className="text-[10pt] text-[#FCFBF7]/80">Parodontologia e laser · pazienti con paura del dentista</p>
      </div>
      <div className="text-right text-[10pt] text-[#FCFBF7]/85 leading-snug">
        <p>Centro Dentistico Galliera</p>
        <p>Via della Pace 41, 40015 Galliera (BO)</p>
        <p>WhatsApp +39 393 518 9869</p>
      </div>
    </footer>
  </article>
);

export default function FlyerPage() {
  const [variant, setVariant] = useState("studio");
  useEffect(() => {
    const m = document.querySelector('meta[name="robots"]');
    const prev = m?.content;
    if (m) m.content = "noindex, nofollow";
    document.title = "Volantino test gengive";
    return () => { if (m) m.content = prev; };
  }, []);
  return (
    <div className="min-h-screen bg-[#E6ECE4] py-10 px-4 print:p-0 print:bg-white" data-testid="flyer-page">
      <div className="no-print max-w-[210mm] mx-auto mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {Object.entries(VARIANTS).map(([k, v]) => (
            <button key={k} onClick={() => setVariant(k)} data-testid={`flyer-variant-${k}`}
              className={`rounded-full px-4 py-2 text-sm border transition-colors ${variant === k ? "bg-[#1B3B28] text-[#FCFBF7] border-[#1B3B28]" : "border-[#1B3B28]/20 text-[#1B3B28] hover:bg-[#FCFBF7]"}`}>{v.label}</button>
          ))}
        </div>
        <button onClick={() => window.print()} data-testid="flyer-print-btn" className="inline-flex items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-5 py-2.5 text-sm hover:bg-[#3E6B48]">
          <Printer size={15} /> Stampa o salva PDF
        </button>
        <p className="w-full text-xs text-[#526356]">Formato A4. Nella finestra di stampa scegli "Salva come PDF" per mandarlo in tipografia, e attiva "Grafica di sfondo".</p>
      </div>
      <div className="overflow-x-auto"><Flyer v={VARIANTS[variant]} /></div>
    </div>
  );
}
